import { z } from 'zod';
import { AI_LIMIT_BOUNDS, AUTO_CATEGORIZE_CONFIDENCE_BOUNDS } from '../constants/ai-limits.js';
import { KPI_TARGET_BOUNDS } from '../constants/kpi-targets.js';
import { WORKSPACE_THRESHOLD_BOUNDS } from '../constants/workspace-thresholds.js';
import { ANOMALY_SETTING_BOUNDS, ANOMALY_SEVERITIES } from '../constants/anomaly-settings.js';
export const fontFamilySchema = z.enum(['inter', 'poppins', 'roboto', 'openSans', 'nunito']);
export const createWorkspaceSchema = z.object({
    name: z.string().min(2).max(255),
    slug: z
        .string()
        .min(2)
        .max(100)
        .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with hyphens'),
    // Optional: create first location
    locationName: z.string().min(2).max(255).optional(),
    // Optional: copy API settings from another workspace
    copyApiSettings: z.boolean().optional(),
    sourceWorkspaceId: z.string().uuid().optional(),
});
export const updateWorkspaceSchema = z.object({
    name: z.string().min(2).max(255).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
});
export const updateBrandingSchema = z.object({
    fontFamily: fontFamilySchema.optional(),
    fontHeading: fontFamilySchema.optional(),
    primaryColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color')
        .optional(),
    accentColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color')
        .optional(),
    borderRadius: z.number().min(0).max(24).optional(),
    compactMode: z.boolean().optional(),
    tagline: z.string().max(120).optional(),
    // Workspace business logo (shown next to the workspace name, replacing the
    // initial-letter avatar). Stored as a relative /uploads path or absolute URL.
    logoUrl: z.string().max(500).optional(),
});
export const createLocationSchema = z.object({
    name: z.string().min(2).max(255),
    address: z.string().max(500).optional(),
    allowedIpRanges: z.array(z.string()).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
});
export const updateLocationSchema = z.object({
    name: z.string().min(2).max(255).optional(),
    address: z.string().max(500).optional(),
    allowedIpRanges: z.array(z.string()).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    isActive: z.boolean().optional(),
});
export const aiConfigSchema = z.object({
    apiKey: z.string().min(10),
    selectedModel: z.string().optional(),
});
const aiLimit = (b) => z.number().int().min(b.min).max(b.max);
/**
 * The limit fields of `PATCH /workspaces/:id/settings`. Omitted means "leave
 * the stored value" — the route used to write 10/32000 for an omitted field.
 */
export const aiLimitsUpdateSchema = z.object({
    aiRateLimitRpm: aiLimit(AI_LIMIT_BOUNDS.rateLimitRpm).optional(),
    aiMaxTokens: aiLimit(AI_LIMIT_BOUNDS.maxTokens).optional(),
});
/** SET-39: the column is `numeric(3, 2)`; a third decimal would be rounded away silently. */
export const autoCategorizeUpdateSchema = z.object({
    aiAutoCategorizeMinConfidence: z
        .number()
        .finite()
        .min(AUTO_CATEGORIZE_CONFIDENCE_BOUNDS.min)
        .max(AUTO_CATEGORIZE_CONFIDENCE_BOUNDS.max)
        .refine((n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6, 'At most two decimal places')
        .optional(),
});
/** Two decimal places at most: the columns are `numeric(_, 2)`, and a third would be rounded away silently. */
const cents = (b) => z.number().finite().min(b.min).max(b.max)
    // `32.3 * 100` is 3229.9999999999995, so an exact comparison refuses a valid 32.3.
    .refine((n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6, 'At most two decimal places');
const targetBand = z
    .object({ min: cents(KPI_TARGET_BOUNDS.percent), max: cents(KPI_TARGET_BOUNDS.percent) })
    .refine((b) => b.min <= b.max, 'The lower target cannot exceed the upper one');
/**
 * The body of `PATCH /workspaces/:id/kpi-targets`. Every field is required:
 * the form always sends the whole set, and a band is only meaningful whole.
 */
export const kpiTargetsUpdateSchema = z.object({
    marginPercent: targetBand,
    laborPercent: targetBand,
    costDriverAlert: cents(KPI_TARGET_BOUNDS.costDriverAlert),
});
/**
 * The body of `PATCH /workspaces/:id/thresholds` (uiv2 R3-3, R3-7). Whole, like
 * the KPI targets: the form always sends both.
 */
export const workspaceThresholdsUpdateSchema = z.object({
    overtimeWeeklyHours: cents(WORKSPACE_THRESHOLD_BOUNDS.overtimeWeeklyHours),
    billerTrendBandPercent: cents(WORKSPACE_THRESHOLD_BOUNDS.billerTrendBandPercent),
});
const ratio = cents(ANOMALY_SETTING_BOUNDS.ratio);
const wholeDays = (b) => z.number().int().min(b.min).max(b.max);
/**
 * The body of `PATCH /workspaces/:id/anomaly-settings` (RP-07). Whole, like the
 * KPI targets: three tiers are only meaningful together, lowest first.
 */
export const anomalySettingsUpdateSchema = z.object({
    ratios: z
        .object({ low: ratio, medium: ratio, high: ratio })
        .refine((r) => r.low <= r.medium && r.medium <= r.high, 'Each tier must be at least the one below it'),
    baselineDays: wholeDays(ANOMALY_SETTING_BOUNDS.baselineDays),
    minHistory: wholeDays(ANOMALY_SETTING_BOUNDS.minHistory),
    newVendor: z.object({
        minAmount: cents(ANOMALY_SETTING_BOUNDS.newVendorMinAmount),
        severity: z.enum(ANOMALY_SEVERITIES),
    }),
});
/**
 * Workspace details (uiv2 BE5, SET-14), sent with the name and slug on
 * `PATCH /workspaces/:id`. Each field is optional — an omitted one is left as
 * stored; an empty legal name or business number clears it.
 */
export const WORKSPACE_PROFILE_BOUNDS = { legalNameMax: 200, businessNumberMax: 30 };
/** A zone this runtime can format in, from its own IANA list (UTC included). */
export function isSupportedTimeZone(tz) {
    return tz === 'UTC' || Intl.supportedValuesOf('timeZone').includes(tz);
}
const clearable = (max, pattern) => z
    .string()
    .trim()
    .max(max)
    .refine((s) => !pattern || pattern.test(s))
    .transform((s) => (s === '' ? null : s))
    .nullable()
    .optional();
export const workspaceProfileUpdateSchema = z.object({
    legalName: clearable(WORKSPACE_PROFILE_BOUNDS.legalNameMax),
    // Letters, digits, spaces and hyphens: "123456789 RT0001", "123456789-RT-0001".
    businessNumber: clearable(WORKSPACE_PROFILE_BOUNDS.businessNumberMax, /^[0-9A-Za-z -]*$/),
    timezone: z.string().refine(isSupportedTimeZone).optional(),
    fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
});
//# sourceMappingURL=workspace.js.map